import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { apiClient } from "@/lib/api/client";
import type { ConnectCarrierPayload, QuoteRate, RateQuoteResponse } from "@/lib/api/types";
import { AVAILABLE_CARRIERS } from "@/lib/carriers";
import { DEFAULT_FLEET_MODE, FLEET_MODE_LABELS, type FleetMode } from "@/lib/tms/fleet-mode";
import { setOnboardingStep } from "@/lib/onboarding";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Globe2,
  Network,
  Package,
  Search,
  Sparkles,
  Truck,
  Warehouse,
  X,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/useSession";

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_STEPS = 6; // 0=Welcome 1=Workspace 2=Carrier 3=Warehouse 4=Quote 5=Done
const DONE_STEP = 5;
const OWN_FLEET_PROGRESS_STEPS = 3;

const SHIPPEEK_LOGO_URL = "/shippeek-mark.svg";

const FEATURED_CODES = ["UPS", "FEDEX", "USPS", "ABF", "ESTES", "LSO"];

const FREIGHT_CLASS_OPTIONS = [
  "50", "55", "60", "65", "70", "77.5", "85", "92.5",
  "100", "125", "150", "175", "200", "250", "300", "400", "500",
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

// ─── Types ────────────────────────────────────────────────────────────────────

type CompletedSteps = {
  carrier: boolean;
  warehouse: boolean;
  quote: boolean;
};

// ─── Top progress bar ─────────────────────────────────────────────────────────

function ProgressBar({ step, total }: { step: number; total: number }) {
  const pct = Math.round((step / (total - 1)) * 100);
  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-[2px] bg-border">
      <div
        className="h-full bg-foreground transition-all duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── Step dots ────────────────────────────────────────────────────────────────

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2" aria-hidden>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "rounded-full transition-all duration-300",
            i < current
              ? "size-2 bg-foreground"
              : i === current
              ? "h-2 w-5 bg-foreground"
              : "size-2 bg-muted-foreground/25",
          )}
        />
      ))}
    </div>
  );
}

// ─── Animated step wrapper ────────────────────────────────────────────────────

function StepFrame({
  children,
  direction = "right",
}: {
  children: React.ReactNode;
  direction?: "left" | "right" | "none";
}) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const startX =
    direction === "right" ? "28px" : direction === "left" ? "-28px" : "0";

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : `translateX(${startX})`,
        transition: "opacity 0.25s ease, transform 0.25s ease",
      }}
    >
      {children}
    </div>
  );
}

// ─── Step 0 — Welcome ─────────────────────────────────────────────────────────

function StepWelcome({
  name,
  email,
  onNext,
  onSkip,
}: {
  name?: string | null;
  email?: string | null;
  onNext: () => void;
  onSkip: () => void;
}) {
  const steps = [
    { icon: Sparkles, label: "Choose your branch" },
    { icon: Network, label: "Show only what fits" },
    { icon: Package, label: "Start working" },
  ];

  return (
    <StepFrame direction="none">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Logo mark */}
          <img src={SHIPPEEK_LOGO_URL} alt="ShipPeek" className="size-14 rounded-2xl shadow-lg" />

        {/* Heading */}
        <div className="space-y-3">
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">
            Welcome to Shippeek
          </h1>
          <p className="text-lg text-muted-foreground max-w-xs mx-auto leading-relaxed">
            Your shipping command center.{" "}
            <span className="text-foreground font-medium">
              First, we’ll keep the app focused on how your team ships.
            </span>
          </p>
        </div>

        {/* User pill */}
        {(name || email) && (
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-sm text-muted-foreground">
            <div className="grid size-5 shrink-0 place-items-center rounded-full bg-foreground text-background text-[10px] font-bold">
              {(name ?? email ?? "U").charAt(0).toUpperCase()}
            </div>
            <span className="truncate max-w-[220px]">{name ?? email}</span>
          </div>
        )}

        {/* Step preview trail */}
        <div className="flex items-center gap-0 w-full max-w-sm mx-auto">
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className="flex flex-col items-center gap-2 flex-1">
                <div className="grid size-9 place-items-center rounded-full border border-border bg-muted/30 text-muted-foreground">
                  <s.icon className="size-4" />
                </div>
                <span className="text-[11px] text-muted-foreground leading-tight text-center">
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className="h-px w-8 bg-border shrink-0 mb-5" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col items-center gap-3 w-full">
          <Button size="lg" className="gap-2 w-full" onClick={onNext}>
            Choose workspace
            <ArrowRight className="size-4" />
          </Button>
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
          >
            Skip setup, take me to the dashboard
          </button>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Step 1 — Workspace Mode ──────────────────────────────────────────────────

const WORKSPACE_MODE_OPTIONS: Array<{
  value: FleetMode;
  label: string;
  eyebrow: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badges: string[];
  bullets: string[];
  recommended?: boolean;
}> = [
  {
    value: "third_party_only",
    label: "Carrier Network",
    eyebrow: "Most popular",
    description: "For teams shipping through general carriers like UPS, FedEx, USPS, Estes, and regional LTL providers.",
    icon: Globe2,
    badges: ["UPS", "FedEx", "USPS", "Estes"],
    bullets: ["Rate shopping and booking", "Tracking and shipment history", "Carrier logos and credentials"],
    recommended: true,
  },
  {
    value: "own_fleet_only",
    label: "Own Fleet TMS",
    eyebrow: "Private fleet",
    description: "For teams managing their own trucks, drivers, trailers, dispatch, and telematics like Samsara or Motive.",
    icon: Truck,
    badges: ["Dispatch", "Fleet", "Samsara", "Motive"],
    bullets: ["Dispatch console", "Fleet registry", "Exception queue"],
  },
];

function StepWorkspaceMode({
  selected,
  saving,
  error,
  onSelect,
  onContinue,
  onBack,
}: {
  selected: FleetMode;
  saving: boolean;
  error: string | null;
  onSelect: (mode: FleetMode) => void;
  onContinue: (mode: FleetMode) => void;
  onBack: () => void;
}) {
  return (
    <StepFrame>
      <div className="space-y-5">
        <div className="space-y-1.5 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Workspace mode</p>
          <h2 className="text-2xl font-semibold tracking-tight">
            How does your team ship?
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            We’ll use this to simplify navigation, routes, and setup. You can change it later in Settings.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {WORKSPACE_MODE_OPTIONS.map((option) => {
            const Icon = option.icon;
            const active = selected === option.value;

            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex min-h-[300px] flex-col rounded-2xl border p-4 text-left transition-[background-color,border-color,box-shadow,transform] duration-150 active:scale-[0.96]",
                  active
                    ? "border-foreground bg-foreground/[0.03] ring-2 ring-foreground"
                    : "border-border hover:border-foreground/30 hover:bg-muted/30",
                )}
                onClick={() => onSelect(option.value)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground">
                    <Icon className="size-5" />
                  </div>
                  <span className={cn(
                    "rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wider",
                    option.recommended
                      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "bg-muted text-muted-foreground",
                  )}>
                    {option.eyebrow}
                  </span>
                </div>

                <div className="mt-4 space-y-2">
                  <h3 className="text-base font-semibold">{option.label}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{option.description}</p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {option.badges.map((badge) => (
                    <span
                      key={badge}
                      className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground"
                    >
                      {badge}
                    </span>
                  ))}
                </div>

                <ul className="mt-auto space-y-2 pt-5 text-sm text-muted-foreground">
                  {option.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center gap-2">
                      <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={onBack}
            disabled={saving}
          >
            <ArrowLeft className="size-3.5" />
            Back
          </button>
          <Button className="gap-2" onClick={() => onContinue(selected)} disabled={saving}>
            {saving ? <Spinner size="sm" /> : null}
            Continue with {FLEET_MODE_LABELS[selected]}
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Step 2 — Connect a Carrier ───────────────────────────────────────────────

function StepCarrier({
  onDone,
  onSkip,
  onBack,
}: {
  onDone: () => void;
  onSkip: () => void;
  onBack: () => void;
}) {
  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState<string | null>(null);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [accountNumber, setAccountNumber] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [connected, setConnected] = React.useState(false);

  const featured = AVAILABLE_CARRIERS.filter((c) =>
    FEATURED_CODES.includes(c.code),
  );
  const searchLower = search.toLowerCase();
  const filtered =
    search.trim()
      ? AVAILABLE_CARRIERS.filter(
          (c) =>
            c.name.toLowerCase().includes(searchLower) ||
            c.code.toLowerCase().includes(searchLower),
        )
      : featured;

  const handleConnect = async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const carrier = AVAILABLE_CARRIERS.find((c) => c.code === selected);
      const payload: ConnectCarrierPayload = {
        carrierCode: selected,
        carrierName: carrier?.name ?? selected,
        username,
        password,
        accountNumber: accountNumber || undefined,
      };
      await apiClient.connectCarrier(payload);
      setConnected(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to connect carrier");
    } finally {
      setLoading(false);
    }
  };

  if (connected) {
    const carrier = AVAILABLE_CARRIERS.find((c) => c.code === selected);
    return (
      <StepFrame>
        <div className="flex flex-col items-center text-center gap-6">
          <div className="relative">
            <CarrierLogo code={carrier?.code ?? selected} name={carrier?.name} className="size-16 rounded-2xl" />
            <span className="absolute -right-1 -top-1 grid size-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 shadow-[0_0_0_2px_hsl(var(--background))] dark:bg-emerald-900 dark:text-emerald-300">
              <Check className="size-3.5" />
            </span>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight">
              Carrier connected!
            </h2>
            <p className="text-muted-foreground">
              {carrier?.name ?? selected} is ready. You'll see live rates
              instantly.
            </p>
          </div>
          <Button size="lg" className="gap-2 w-full" onClick={onDone}>
            Continue
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </StepFrame>
    );
  }

  return (
    <StepFrame>
      <div className="space-y-5">
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight">
            Connect your first carrier
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Shippeek works with 50+ LTL and parcel carriers. Add credentials to
            start getting live rates.
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search carriers…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected(null);
            }}
            className="pl-9 pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        {/* Carrier grid */}
        <div className="grid grid-cols-3 gap-2.5 max-h-56 overflow-y-auto pr-1 -mr-1">
          {filtered.length === 0 && (
            <div className="col-span-3 py-6 text-center text-sm text-muted-foreground">
              No carriers found
            </div>
          )}
          {filtered.map((carrier) => (
            <button
              key={carrier.code}
              type="button"
              onClick={() =>
                setSelected(selected === carrier.code ? null : carrier.code)
              }
              className={cn(
                "flex flex-col items-center gap-2 rounded-xl border p-3.5 text-sm font-medium transition-all duration-150 text-left",
                selected === carrier.code
                  ? "border-foreground bg-foreground/5 shadow-sm ring-1 ring-foreground/10"
                  : "border-border hover:border-foreground/30 hover:bg-muted/40",
              )}
            >
              <CarrierLogo code={carrier.code} name={carrier.name} className="size-10" />
              <span className="truncate w-full text-center text-[12px] leading-snug">
                {carrier.name}
              </span>
            </button>
          ))}
        </div>

        {/* Inline credential form */}
        {selected && (
          <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CarrierLogo
                code={selected}
                name={AVAILABLE_CARRIERS.find((c) => c.code === selected)?.name}
                className="size-8 rounded-md"
              />
              <p className="text-sm font-medium text-foreground">
                {AVAILABLE_CARRIERS.find((c) => c.code === selected)?.name} credentials
              </p>
            </div>
            <div className="grid gap-2.5">
              <div className="space-y-1">
                <Label htmlFor="c-username" className="text-xs">
                  Username
                </Label>
                <Input
                  id="c-username"
                  placeholder="Username or User ID"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="c-password" className="text-xs">
                  Password
                </Label>
                <Input
                  id="c-password"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="c-account" className="text-xs">
                  Account number{" "}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="c-account"
                  placeholder="Account number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              className="w-full gap-2"
              onClick={() => void handleConnect()}
              disabled={loading || !username || !password}
            >
              {loading ? <Spinner size="sm" /> : <Truck className="size-4" />}
              {loading ? "Connecting…" : "Connect Carrier"}
            </Button>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={onBack}
          >
            <ArrowLeft className="size-3.5" />
            Back
          </button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
            onClick={onSkip}
          >
            Skip for now →
          </button>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Step 2 — Add Warehouse / Ship-From Address ───────────────────────────────

function StepWarehouse({
  onDone,
  onSkip,
  onBack,
}: {
  onDone: () => void;
  onSkip: () => void;
  onBack: () => void;
}) {
  const [companyName, setCompanyName] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  const canSubmit = address1.trim() && city.trim() && state && zip.trim();

  const handleSave = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      await apiClient.createAddress({
        company: companyName || undefined,
        name: contactName || companyName || `${address1}, ${city}`,
        address: address1,
        address2: address2 || undefined,
        city,
        state,
        zip,
        country: "US",
        phone: phone || undefined,
      });
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save address");
    } finally {
      setLoading(false);
    }
  };

  if (saved) {
    return (
      <StepFrame>
        <div className="flex flex-col items-center text-center gap-6">
          <div className="grid size-14 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
            <Check className="size-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight">
              Address saved!
            </h2>
            <p className="text-muted-foreground">
              {companyName ? `${companyName} · ` : ""}
              {city}, {state} {zip} is your default ship-from location.
            </p>
          </div>
          <Button size="lg" className="gap-2 w-full" onClick={onDone}>
            Continue
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </StepFrame>
    );
  }

  return (
    <StepFrame>
      <div className="space-y-5">
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight">
            Where are you shipping from?
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Add your warehouse or default ship-from address.
          </p>
        </div>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="wh-company" className="text-xs">
                Company Name
              </Label>
              <Input
                id="wh-company"
                placeholder="Acme Corp"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wh-contact" className="text-xs">
                Contact Name
              </Label>
              <Input
                id="wh-contact"
                placeholder="Jane Smith"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wh-addr1" className="text-xs">
              Address Line 1 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="wh-addr1"
              placeholder="1234 Commerce Drive"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wh-addr2" className="text-xs">
              Address Line 2
            </Label>
            <Input
              id="wh-addr2"
              placeholder="Suite 100 (optional)"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="wh-city" className="text-xs">
                City <span className="text-destructive">*</span>
              </Label>
              <Input
                id="wh-city"
                placeholder="Houston"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wh-state" className="text-xs">
                State <span className="text-destructive">*</span>
              </Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger id="wh-state" className="w-full">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wh-zip" className="text-xs">
                ZIP <span className="text-destructive">*</span>
              </Label>
              <Input
                id="wh-zip"
                placeholder="77001"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wh-phone" className="text-xs">
              Phone
            </Label>
            <Input
              id="wh-phone"
              placeholder="(555) 000-0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          className="w-full gap-2"
          onClick={() => void handleSave()}
          disabled={loading || !canSubmit}
        >
          {loading ? <Spinner size="sm" /> : <Warehouse className="size-4" />}
          {loading ? "Saving…" : "Save Address"}
        </Button>

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={onBack}
          >
            <ArrowLeft className="size-3.5" />
            Back
          </button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
            onClick={onSkip}
          >
            Skip for now →
          </button>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Step 3 — Try a Sample Shipment ──────────────────────────────────────────

type QuoteField = {
  destCity: string;
  destState: string;
  destZip: string;
  weight: string;
  freightClass: string;
  handlingUnits: string;
};

function StepSampleQuote({
  onDone,
  onSkip,
  onBack,
}: {
  onDone: () => void;
  onSkip: () => void;
  onBack: () => void;
}) {
  const [fields, setFields] = React.useState<QuoteField>({
    destCity: "Austin",
    destState: "TX",
    destZip: "78701",
    weight: "500",
    freightClass: "70",
    handlingUnits: "1",
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<RateQuoteResponse | null>(null);

  const set = (k: keyof QuoteField) => (val: string) =>
    setFields((f) => ({ ...f, [k]: val }));

  const canSubmit =
    fields.destCity.trim() && fields.destState && fields.destZip.trim();

  const handleGetRates = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiClient.quoteLtl({
        originCompany: "Shippeek Demo Warehouse",
        originAddress: "1600 E Lamar Blvd",
        originCity: "Arlington",
        originState: "TX",
        originPostalCode: "76006",
        originCountry: "US",
        destCity: fields.destCity,
        destState: fields.destState,
        destPostalCode: fields.destZip,
        destAddress: "",
        destCountry: "US",
        items: [
          {
            handlingUnits: parseInt(fields.handlingUnits, 10) || 1,
            weight: parseInt(fields.weight, 10) || 500,
            freightClass: parseFloat(fields.freightClass) || 70,
          },
        ],
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to get rates");
    } finally {
      setLoading(false);
    }
  };

  const topRates = result?.rates?.slice(0, 3) ?? [];

  return (
    <StepFrame>
      <div className="space-y-5">
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight">
            See Shippeek in action
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            We've pre-filled a sample shipment from our Texas demo warehouse.
            Get a live rate quote in seconds.
          </p>
        </div>

        {/* Locked origin */}
        <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
          <div className="p-4 bg-muted/20">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Origin (locked)
            </p>
            <p className="text-sm font-medium">Shippeek Demo Warehouse</p>
            <p className="text-sm text-muted-foreground">
              1600 E Lamar Blvd · Arlington, TX 76006
            </p>
          </div>

          {/* Destination fields */}
          <div className="p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Destination
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1 space-y-1">
                <Label htmlFor="sq-city" className="text-xs">
                  City <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="sq-city"
                  placeholder="Austin"
                  value={fields.destCity}
                  onChange={(e) => set("destCity")(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sq-state" className="text-xs">
                  State <span className="text-destructive">*</span>
                </Label>
                <Select value={fields.destState} onValueChange={set("destState")}>
                  <SelectTrigger id="sq-state" className="w-full">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sq-zip" className="text-xs">
                  ZIP <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="sq-zip"
                  placeholder="78701"
                  value={fields.destZip}
                  onChange={(e) => set("destZip")(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Freight */}
          <div className="p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Freight
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label htmlFor="sq-hu" className="text-xs">
                  Pallets
                </Label>
                <Select
                  value={fields.handlingUnits}
                  onValueChange={set("handlingUnits")}
                >
                  <SelectTrigger id="sq-hu" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["1","2","3","4","5","6","8","10"].map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sq-weight" className="text-xs">
                  Weight (lbs)
                </Label>
                <Input
                  id="sq-weight"
                  type="number"
                  min="1"
                  placeholder="500"
                  value={fields.weight}
                  onChange={(e) => set("weight")(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sq-class" className="text-xs">
                  Freight Class
                </Label>
                <Select
                  value={fields.freightClass}
                  onValueChange={set("freightClass")}
                >
                  <SelectTrigger id="sq-class" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREIGHT_CLASS_OPTIONS.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Inline results */}
        {topRates.length > 0 && (
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10 divide-y divide-emerald-100 dark:divide-emerald-900/30 overflow-hidden">
            <div className="px-4 py-2.5 flex items-center gap-2">
              <Check className="size-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                {result!.rates.length} rates found
              </span>
            </div>
            {topRates.map((rate: QuoteRate) => (
              <div
                key={rate.ref}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <CarrierLogo code={rate.carrierCode} name={rate.carrier} className="size-8 rounded-md" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{rate.carrier}</p>
                    <p className="text-xs text-muted-foreground">
                      {rate.serviceType}
                      {rate.days ? ` · ${rate.days} days` : ""}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold">
                  ${rate.total.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive rounded-lg bg-destructive/5 border border-destructive/20 px-3 py-2">
            {error}
          </p>
        )}

        <div className="space-y-3">
          {result ? (
            <Button
              size="lg"
              className="w-full gap-2"
              onClick={onDone}
            >
              Continue
              <ArrowRight className="size-4" />
            </Button>
          ) : (
            <>
              <Button
                size="lg"
                className="w-full gap-2"
                onClick={() => void handleGetRates()}
                disabled={loading || !canSubmit}
              >
                {loading ? <Spinner size="sm" /> : <Package className="size-4" />}
                {loading ? "Fetching rates…" : "Get Sample Quote →"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Real rates from your connected carriers. No commitment.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={onBack}
          >
            <ArrowLeft className="size-3.5" />
            Back
          </button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
            onClick={onSkip}
          >
            Skip for now →
          </button>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Step 4 — You're all set ──────────────────────────────────────────────────

function StepDone({
  completed,
  fleetMode,
  onFinish,
}: {
  completed: CompletedSteps;
  fleetMode: FleetMode;
  onFinish: () => void;
}) {
  const isOwnFleet = fleetMode === "own_fleet_only";
  const items = isOwnFleet
    ? [
        { label: "Selected Own Fleet TMS", done: true },
        { label: "Dispatch, Fleet, and Exceptions enabled", done: true },
        { label: "Carrier Network pages hidden", done: true },
      ]
    : [
        { label: "Selected Carrier Network", done: true },
        { label: "Connected a carrier", done: completed.carrier },
        { label: "Added a warehouse address", done: completed.warehouse },
        { label: "Tried a sample rate quote", done: completed.quote },
      ];

  const doneCount = items.filter((i) => i.done).length;

  return (
    <StepFrame>
      <div className="flex flex-col items-center text-center gap-7">
        <div className="space-y-2">
          <div className="text-4xl">
            {isOwnFleet || doneCount === items.length ? "🎉" : doneCount > 1 ? "✅" : "👋"}
          </div>
          <div className="inline-flex items-center rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
            {FLEET_MODE_LABELS[fleetMode]}
          </div>
          <h2 className="text-3xl font-semibold tracking-tight">
            You're all set!
          </h2>
          <p className="text-muted-foreground text-sm">
            {isOwnFleet
              ? "Your focused TMS workspace is ready for dispatch, fleet, exceptions, and telematics."
              : doneCount === items.length
              ? "You completed the full setup — you're ready to ship."
              : doneCount > 1
              ? "You're making great progress. You can finish setup anytime."
              : "Your Carrier Network workspace is waiting. You can always set up later."}
          </p>
        </div>

        <div className="w-full rounded-xl border border-border divide-y divide-border overflow-hidden text-left">
          {items.map((item) => (
            <div key={item.label} className="flex items-center gap-3 px-4 py-3.5">
              <div
                className={cn(
                  "grid size-5 shrink-0 place-items-center rounded-full",
                  item.done
                    ? "bg-emerald-100 dark:bg-emerald-900/30"
                    : "bg-muted",
                )}
              >
                <Check
                  className={cn(
                    "size-3",
                    item.done
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-muted-foreground/25",
                  )}
                />
              </div>
              <span
                className={cn(
                  "text-sm flex-1",
                  item.done
                    ? "text-foreground font-medium"
                    : "text-muted-foreground",
                )}
              >
                {item.label}
              </span>
              {!item.done && (
                <span className="text-xs text-muted-foreground/50">
                  skipped
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col w-full gap-3">
          <Button
            size="lg"
            className="w-full gap-2"
            onClick={onFinish}
          >
            {isOwnFleet ? "Open Dispatch Console" : "Start Shipping"}
            <ArrowRight className="size-4" />
          </Button>
          <p className="text-xs text-muted-foreground">
            You can switch workspace modes later from Account → Settings.
          </p>
        </div>
      </div>
    </StepFrame>
  );
}

// ─── Main Onboarding Page ─────────────────────────────────────────────────────

export default function OnboardingPage() {
  const { user } = useSession();
  const navigate = useNavigate();

  const [step, setStep] = React.useState(0);
  const [selectedMode, setSelectedMode] = React.useState<FleetMode>(DEFAULT_FLEET_MODE);
  const [modeSaving, setModeSaving] = React.useState(false);
  const [modeError, setModeError] = React.useState<string | null>(null);
  const [completed, setCompleted] = React.useState<CompletedSteps>({
    carrier: false,
    warehouse: false,
    quote: false,
  });

  React.useEffect(() => {
    setOnboardingStep(step);
  }, [step]);

  const go = (n: number) => {
    setStep(n);
  };

  const goNext = () => go(step + 1);
  const goBack = () => go(Math.max(step - 1, 0));

  const saveWorkspaceMode = async (mode: FleetMode): Promise<boolean> => {
    setModeSaving(true);
    setModeError(null);
    try {
      const response = await fetch("/api/tms/settings", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fleet_mode: mode }),
      });
      const json = await response.json();
      if (json.status !== "success") {
        setModeError(json.error ?? "Unable to save workspace mode.");
        return false;
      }
      return true;
    } catch {
      setModeError("Unable to save workspace mode.");
      return false;
    } finally {
      setModeSaving(false);
    }
  };

  const handleWorkspaceContinue = async (mode: FleetMode) => {
    setSelectedMode(mode);
    const saved = await saveWorkspaceMode(mode);
    if (!saved) return;

    if (mode === "own_fleet_only") {
      go(DONE_STEP);
      return;
    }

    goNext();
  };

  const finishOnboarding = () => {
    // Fire-and-forget — navigate immediately; server marks completion async
    apiClient.completeOnboarding().catch(() => {});
    navigate(selectedMode === "own_fleet_only" ? "/dispatch" : "/quotes", { replace: true });
  };

  const skipAll = finishOnboarding;

  const handleCarrierDone = () => {
    setCompleted((c) => ({ ...c, carrier: true }));
    goNext();
  };
  const handleWarehouseDone = () => {
    setCompleted((c) => ({ ...c, warehouse: true }));
    goNext();
  };
  const handleQuoteDone = () => {
    setCompleted((c) => ({ ...c, quote: true }));
    goNext();
  };

  // Step labels for the header (excluding welcome & done)
  const progressTotal = selectedMode === "own_fleet_only" && step === DONE_STEP
    ? OWN_FLEET_PROGRESS_STEPS
    : TOTAL_STEPS;
  const progressStep = selectedMode === "own_fleet_only" && step === DONE_STEP
    ? OWN_FLEET_PROGRESS_STEPS - 1
    : step;
  const setupStepCount = selectedMode === "own_fleet_only" ? 1 : TOTAL_STEPS - 2;
  const stepLabel =
    step > 0 && step < DONE_STEP
      ? `Step ${Math.min(step, setupStepCount)} of ${setupStepCount}`
      : null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <ProgressBar step={progressStep} total={progressTotal} />

      {/* Minimal header */}
      <header className="flex h-12 shrink-0 items-center justify-between px-5 border-b border-border/50">
        <div className="flex items-center gap-2">
          <img src={SHIPPEEK_LOGO_URL} alt="ShipPeek" className="size-6 rounded-md select-none" />
          <span className="text-sm font-semibold tracking-tight">
            Shippeek
          </span>
        </div>

        {stepLabel && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {stepLabel}
          </span>
        )}

        <StepDots current={progressStep} total={progressTotal} />
      </header>

      {/* Centered content area */}
      <main className="flex flex-1 items-center justify-center p-6 py-10">
        <div className={cn("w-full", step === 1 ? "max-w-3xl" : "max-w-lg")}>
          {step === 0 && (
            <StepWelcome
              name={user?.name}
              email={user?.email}
              onNext={goNext}
              onSkip={skipAll}
            />
          )}
          {step === 1 && (
            <StepWorkspaceMode
              selected={selectedMode}
              saving={modeSaving}
              error={modeError}
              onSelect={setSelectedMode}
              onContinue={(mode) => void handleWorkspaceContinue(mode)}
              onBack={goBack}
            />
          )}
          {step === 2 && (
            <StepCarrier
              onDone={handleCarrierDone}
              onSkip={goNext}
              onBack={goBack}
            />
          )}
          {step === 3 && (
            <StepWarehouse
              onDone={handleWarehouseDone}
              onSkip={goNext}
              onBack={goBack}
            />
          )}
          {step === 4 && (
            <StepSampleQuote
              onDone={handleQuoteDone}
              onSkip={goNext}
              onBack={goBack}
            />
          )}
          {step === DONE_STEP && (
            <StepDone completed={completed} fleetMode={selectedMode} onFinish={finishOnboarding} />
          )}
        </div>
      </main>
    </div>
  );
}
